import { getCourtesyStringFromContent } from '@service/bugo/containers/MemberCourtesy/courtesyGenerator';
import { generateFallbackCourtesyContentList } from '@service/bugo/utils/courtesyContentGenerator';
import { BugoData } from '@shared/api/bugo/bugo.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

/**
 * 답례 인사 발송 모달의 "자주 쓰는 문구" — 누르면 본문에 바로 들어간다.
 * 본문 아래에는 서버가 "답례품 받기 + 링크"를 자동으로 붙이므로, 문구는 전부
 * "아래 '답례품 받기' 링크"를 가리키도록 쓴다. (링크 자체는 문구에 넣지 않는다)
 */

/** 재발송 안내 — 답례품센터 명의로 신청 링크를 다시 보낼 때 */
export const COURTESY_RESEND_MESSAGE = [
  '안녕하세요, 답례품센터입니다.',
  '',
  '상주님께서 감사의 마음을 담아 준비하신 답례품을 받으실 수 있도록 신청 링크를 다시 보내드립니다.',
  '',
  '[받는 방법]',
  "1. 아래 '답례품 받기' 링크를 눌러 주세요.",
  '2. 안내에 따라 답례품(기프티콘)을 신청해 주세요.',
  '3. 신청하신 휴대폰 번호로 기프티콘을 문자 또는 카카오톡(알림톡)으로 보내드립니다.',
  '',
  '[꼭 확인해 주세요]',
  '- 기프티콘은 받으신 날부터 약 2개월 동안 사용하실 수 있습니다. (상품마다 기간이 다를 수 있습니다)',
  '- 기간이 지나면 사용하실 수 없으니, 받으신 뒤 기간 안에 꼭 사용해 주세요.',
  '',
  '감사합니다.',
].join('\n');

/** 상주 답례글 밑에 붙는 사용안내 */
export const COURTESY_USAGE_GUIDE = [
  '[답례품 받는 방법]',
  "아래 '답례품 받기' 링크에서 신청하시면, 신청하신 휴대폰 번호로 기프티콘을 문자 또는 카카오톡(알림톡)으로 보내드립니다.",
  '',
  '[꼭 확인해 주세요]',
  '- 기프티콘은 받으신 날부터 약 2개월 동안 사용하실 수 있습니다. (상품마다 기간이 다를 수 있습니다)',
  '- 기간이 지나면 사용하실 수 없으니, 받으신 뒤 기간 안에 꼭 사용해 주세요.',
].join('\n');

export interface MournerCourtesyPreset {
  text: string;
  /** saved = 상주가 전달 페이지에서 저장한 답례글, default = 저장분이 없어 표준 예문으로 만든 것 */
  source: 'saved' | 'default';
}

/**
 * 상주 대리 문구 머리말 — 누가 보낸 문자인지부터 밝힌다.
 * 발신번호가 답례품센터라 받는 분이 모르는 번호 + 링크 = 스팸으로 오해하기 쉬워,
 * 답례글 바로 위에 보낸 분 이름을 제목처럼 붙인다. (고인명·"상주" 호칭 없이 이름만)
 */
export const buildMournerIntro = (memberName?: string): string =>
  `[${memberName ? `${memberName}님` : '유가족'}의 답례인사]`;

/**
 * 상주 대리 — 머리말(누가 보냈는지) + 상주가 답례품을 직접 전달할 때 나가는
 * 답례글("○○ 배상") + 사용안내.
 * 상주가 저장한 답례글(order.courtesyContent)이 있으면 그대로, 없으면 스토어 기본과 같은
 * 표준 예문에 고인명·상주명을 채운다. (이름을 모르면 '가족 장례'/'유가족'으로 치환)
 */
export const buildMournerCourtesyPreset = (
  order: GiftOrderForAdmin | undefined,
): MournerCourtesyPreset | null => {
  if (!order) return null;

  //* 관리자 주문 조회는 bugo를 populate하지만, 안 된 응답(문자열 id)도 방어한다
  const bugo = typeof order.bugo === 'object' ? order.bugo : undefined;
  const memberName = bugo?.member?.fullName?.trim();
  const deceasedName = (
    bugo?.fevent?.deceasedInfo?.name ?? order.fevent?.deceasedInfo?.name
  )?.trim();
  //* 머리말은 답례글 바로 위에 붙이고, 사용안내는 한 줄 띄워 붙인다
  const compose = (courtesy: string) =>
    `${buildMournerIntro(memberName)}\n${courtesy}\n\n${COURTESY_USAGE_GUIDE}`;

  const saved = order.courtesyContent?.trim();
  if (saved) return { text: compose(saved), source: 'saved' };

  const standard = generateFallbackCourtesyContentList()[0];
  if (!standard) return null;

  const bugoLike = {
    member: { fullName: memberName },
    fevent: { deceasedInfo: { name: deceasedName } },
  } as unknown as BugoData;
  const courtesy = getCourtesyStringFromContent(
    standard,
    bugoLike,
    false,
    !memberName || !deceasedName,
  );

  return { text: compose(courtesy), source: 'default' };
};

/** LMS 본문 한도(byte) — NCP SENS LMS 기준 */
export const LMS_MAX_BYTES = 2000;

/** LMS 과금 기준(EUC-KR) 근사 바이트 수 — 한글 등 비ASCII는 2byte */
export const lmsByteLength = (text: string): number => {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) bytes += text.charCodeAt(i) <= 0x7f ? 1 : 2;
  return bytes;
};
